import React, { useEffect, useRef, useState } from 'react';
import { SvgIcon } from 'core/icons';
import { Translate } from 'core/translate';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';

const MoreViewsControlIcon = ({ hovered, isToggled, icons }) => {
  const toggleIcon = isToggled ? icons.trueIcn : icons.falseIcn;
  const toggleIconName = hovered ? `${toggleIcon}-hover` : toggleIcon;
  return <SvgIcon className="svg-icon" name={toggleIconName} />;
};

const MoreViewsControl = ({
  action,
  label,
  onToggle,
  icons,
  isToggled,
  val,
  disableControls,
  analyticsKey,
}) => {
  const [hovered, setHovered] = useState(false);
  const ctrlRef = useRef(null);

  const manageHover = (hovering = false) => {
    setHovered(hovering);
  };

  return (
    <div className="more-views__toggle" data-value={val + 1}>
      <button
        disabled={disableControls}
        className="more-views__toggle-btn stat-button-link"
        onClick={() => onToggle({ id: action, request: isToggled })}
        onMouseEnter={() => manageHover(true)}
        onMouseLeave={() =>
          manageHover(ctrlRef.current === document.activeElement)
        }
        onFocus={() => manageHover(true)}
        onBlur={() => manageHover()}
        ref={ctrlRef}
        data-dtm={analyticsKey}>
        <MoreViewsControlIcon
          icons={icons}
          hovered={hovered}
          isToggled={isToggled}
        />
        <span className="more-views__toggle-text">{label}</span>
      </button>
    </div>
  );
};

const MoreViewsMenu = ({
  onClose,
  controls,
  onToggle,
  disableControls,
  analyticsKey,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [togglesHasOverflow, setTogglesHasOverflow] = useState(false);
  const [carouselItems, setCarouselItems] = useState([]);
  const togglesRef = useRef(null);

  const adjustIndexQuery = window.matchMedia(
    'only screen and (min-width: 430px)'
  ).matches;

  const displayRightArrow =
    togglesHasOverflow && activeIndex < carouselItems.length - 1;
  const displayLeftArrow = togglesHasOverflow && activeIndex > 0;

  const slidePrev = () => {
    if (adjustIndexQuery) {
      setActiveIndex(0);
    } else {
      setActiveIndex(activeIndex - 1);
    }
  };

  const slideNext = () => {
    if (adjustIndexQuery) {
      setActiveIndex(carouselItems.length - 1);
    } else {
      setActiveIndex(activeIndex + 1);
    }
  };

  useEffect(() => {
    const accessibilityCtrls = ({ key }) => {
      switch (key) {
        case 'ArrowLeft':
          if (displayLeftArrow) {
            slidePrev();
          }
          break;
        case 'ArrowRight':
          if (displayRightArrow) {
            slideNext();
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', accessibilityCtrls);
    return () => {
      window.removeEventListener('keydown', accessibilityCtrls);
    };
  }, [displayLeftArrow, displayRightArrow]);

  useEffect(() => {
    const aliceStagingEl =
      togglesRef.current &&
      togglesRef.current.querySelector('.alice-carousel__stage');
    const setOverflow = () => {
      setTogglesHasOverflow(
        aliceStagingEl.offsetWidth < aliceStagingEl.scrollWidth
      );
    };
    const carouselResizeObserver = new ResizeObserver(setOverflow);
    carouselResizeObserver.observe(aliceStagingEl);

    return () => {
      carouselResizeObserver.unobserve(aliceStagingEl);
    };
  }, [carouselItems]);

  useEffect(() => {
    const controlItems = controls.map(
      ({ labels, messageAction, isToggled, icons }, index) => (
        <MoreViewsControl
          key={messageAction}
          val={index}
          icons={icons}
          onToggle={onToggle}
          isToggled={isToggled}
          action={messageAction}
          label={
            isToggled ? (
              <Translate value={labels.trueLbl} />
            ) : (
              <Translate value={labels.falseLbl} />
            )
          }
          disableControls={disableControls}
          analyticsKey={analyticsKey}
        />
      )
    );
    setCarouselItems(controlItems);
  }, [controls, disableControls]);

  const navStateSync = (e) => {
    setActiveIndex(e.slide);
  };

  return (
    <nav className="more-views-menu">
      <div className="more-views-menu__close">
        <button
          className="more-views-menu__close-btn stat-button-close"
          data-dtm={analyticsKey}
          onClick={onClose}>
          <SvgIcon
            className="more-views-menu__close-icon"
            name="more-views-menu-close"
          />
          <span className="more-views-menu__close-text">
            <Translate value="byo.page.general.close" />
          </span>
        </button>
      </div>
      {carouselItems && (
        <div className="more-views-toggles" ref={togglesRef}>
          <AliceCarousel
            autoWidth
            activeIndex={activeIndex}
            disableDotsControls
            disableButtonsControls
            touchMoveDefaultEvents={togglesHasOverflow}
            items={carouselItems}
            onSlideChanged={navStateSync}
            onResized={navStateSync}
          />
          {displayLeftArrow && (
            <div
              className="more-views-toggles__button-container more-views-toggles__button-container--left stat-arrow-prev"
              data-dtm={analyticsKey}>
              <button onClick={slidePrev}>
                <SvgIcon
                  name="chevron-left"
                  className="more-views-toggles__chevron"
                />
              </button>
            </div>
          )}
          {displayRightArrow && (
            <div
              className="more-views-toggles__button-container more-views-toggles__button-container--right stat-arrow-next"
              data-dtm={analyticsKey}>
              <button onClick={slideNext}>
                <SvgIcon
                  name="chevron-right"
                  className="more-views-toggles__chevron"
                />
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default MoreViewsMenu;
